import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getFeaturedArticleDetails,
  getFeaturedArticleDiscussion,
  getFeaturedArticleReviews,
} from '../actions/featuredArticlesActions';
import { addBlogArticleDiscussion } from '../../discussions/actions/discussionActions';
// import { addBlogArticleDiscussion } from '../../features/discussions/actions/discussionActions';
// import { addBlogArticleDiscussion } from '../actions/discussionActions';

// Define the state structure for the single article page
interface FeaturedArticleDetailStateType {
  currentArticle: BlogCard | null;
  reviews: BlogReviewsListSuccessType | null;
  discussions: BlogDiscussionsListSuccessType | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: FeaturedArticleDetailStateType = {
  currentArticle: null,
  reviews: null,
  discussions: null,
  isLoading: false,
  error: null,
};

const featuredArticleDetailSlice = createSlice({
  name: 'featuredArticleDetail',
  initialState,
  reducers: {
    resetArticleState: () => initialState,
  },
  extraReducers: builder => {
    featuredArticleDetails(builder);
    featuredArticleReviews(builder);
    featuredArticleDiscussions(builder);
    addBlogArticleDiscussionReducer(builder);
  },
});

// Named reducer for article details
const featuredArticleDetails = (
  builder: ActionReducerMapBuilder<FeaturedArticleDetailStateType>
) => {
  builder.addCase(getFeaturedArticleDetails.pending, state => {
    state.isLoading = true;
    state.error = null;
  });

  builder.addCase(
    getFeaturedArticleDetails.fulfilled,
    (state, action: PayloadAction<BlogCard>) => {
      state.isLoading = false;
      state.currentArticle = action.payload;
    }
  );

  builder.addCase(getFeaturedArticleDetails.rejected, (state, action) => {
    state.isLoading = false;
    state.error = action.payload?.detail || 'Failed to fetch article details.';
  });
};

// Named reducer for article reviews
const featuredArticleReviews = (
  builder: ActionReducerMapBuilder<FeaturedArticleDetailStateType>
) => {
  builder.addCase(
    getFeaturedArticleReviews.fulfilled,
    (state, action: PayloadAction<BlogReviewsListSuccessType>) => {
      state.reviews = action.payload;
    }
  );
};

// Named reducer for article discussions
const featuredArticleDiscussions = (
  builder: ActionReducerMapBuilder<FeaturedArticleDetailStateType>
) => {
  builder.addCase(
    getFeaturedArticleDiscussion.fulfilled,
    (state, action: PayloadAction<BlogDiscussionsListSuccessType>) => {
      // Filter out null results before storing
      const filteredResults =
        action.payload.results?.filter(
          discussion => discussion !== null && discussion !== undefined
        ) || [];

      state.discussions = {
        ...action.payload,
        results: filteredResults,
      };
    }
  );
};

// Named reducer for adding new discussions
const addBlogArticleDiscussionReducer = (
  builder: ActionReducerMapBuilder<FeaturedArticleDetailStateType>
) => {
  builder.addCase(
    addBlogArticleDiscussion.fulfilled,
    (
      state: FeaturedArticleDetailStateType,
      action: PayloadAction<{
        newDiscussion: any;
        contentType: 'blog' | 'article' | 'clearing';
        contentId: number;
      }>
    ) => {
      const { newDiscussion, contentType } = action.payload;

      // Only update if this is an article discussion AND newDiscussion is not null
      if (contentType === 'article' && newDiscussion && state.discussions) {
        const discussionToAdd = {
          id: newDiscussion.id,
          title: newDiscussion.title,
          content: newDiscussion.content,
          content_slug: newDiscussion.content_slug || '',
          content_title: newDiscussion.content_title || '',
          content_id: newDiscussion.content_id || newDiscussion.article || 0,
          content_type: newDiscussion.content_type || 'article',
          user: newDiscussion.user,
          created_on: newDiscussion.created_on,
          comment_count: newDiscussion.comment_count || 0,
        } as DiscussionDetailsType;

        // Add the new discussion to the beginning of the list
        state.discussions.results.unshift(discussionToAdd);
        state.discussions.count += 1;
      }
    }
  );
};

export const { resetArticleState } = featuredArticleDetailSlice.actions;
export default featuredArticleDetailSlice.reducer;